import type { Maybe, VoyageCharterOrderFreight } from '../graphql';
import { Currency } from '../graphql';
import { nonWeakMemoize3 } from '@seaber/common/lib/memoize';

// ISO 80000-1: NBSP thousands separator, dot decimal separator.
// fi-FI gives us NBSP thousands and suffix-style currency symbols natively;
// we swap its comma decimal for a dot, normalize the various NBSPs/spaces
// to U+00A0, and replace the Unicode minus sign with ASCII '-'.
const DEFAULT_LOCALE: Intl.LocalesArgument = 'fi-FI';
export const NBSP = ' ';

/** Shown in place of a value that is unknown, as opposed to a value that is zero. */
export const UNKNOWN_VALUE = '—';

const applyIsoSeparators = (
  formatter: Intl.NumberFormat,
  value: number,
): string =>
  formatter
    .formatToParts(value)
    .map((p) => {
      if (p.type === 'decimal') return '.';
      if (p.type === 'group') return NBSP;
      if (p.type === 'minusSign') return '-';
      if (p.type === 'literal') return p.value.replace(/\s/g, NBSP);
      return p.value;
    })
    .join('');

const getNumberFormatter = nonWeakMemoize3(function getNumberFormatter(
  fractionDigits: number,
  significantDigits: number | undefined = undefined,
) {
  return new Intl.NumberFormat(DEFAULT_LOCALE, {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
    minimumSignificantDigits: significantDigits,
    maximumSignificantDigits: significantDigits,
  });
});

const getCurrencyFormatter = nonWeakMemoize3(function getCurrencyFormatter(
  currency: Currency,
  fractionDigits: number,
  signDisplay?: Intl.NumberFormatOptions['signDisplay'],
) {
  return new Intl.NumberFormat(DEFAULT_LOCALE, {
    style: 'currency',
    currency,
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
    signDisplay,
  });
});

export const getNumberFormatter2 = nonWeakMemoize3(function getNumberFormatter2(
  minFractionDigits: number,
  maxFractionDigits: number,
  minSignificantDigits: number | undefined = undefined,
  maxSignificantDigits: number | undefined = undefined,
) {
  const formatter = new Intl.NumberFormat(DEFAULT_LOCALE, {
    minimumFractionDigits: minFractionDigits,
    maximumFractionDigits: maxFractionDigits,
    minimumSignificantDigits: minSignificantDigits,
    maximumSignificantDigits: maxSignificantDigits,
  });
  return {
    format: (value: number) => applyIsoSeparators(formatter, value),
  };
});

export const formatNumber = (
  number: number | null,
  fractionDigits = 0,
  significantDigits: number | undefined = undefined,
): string | null => {
  if (number === null || isNaN(number)) return null;
  return applyIsoSeparators(
    getNumberFormatter(fractionDigits, significantDigits),
    number,
  );
};

/**
 * Round a numeric value to at most `maxDecimals` fractional digits, matching
 * the rounding `<Numeric maxDecimals={N}>` applies on display. Use for form
 * inputs so the persisted value stays consistent with what the user sees.
 *
 * Scaling via the shortest round-trip string (not `value.toFixed`) decides the
 * half-way tie from the typed value, not the IEEE-754 double: 0.1234565 is
 * stored as 0.12345649999…, so `toFixed(6)` would wrongly round the tie DOWN.
 */
export const roundToMaxDecimals = (
  value: number,
  maxDecimals: number,
): number => {
  if (!isFinite(value)) return value;
  // Split off any existing exponent first, so tiny values that stringify as
  // e.g. "5e-7" don't build a malformed "5e-7e6" (→ NaN) when we shift up.
  const [coefficient, exponent = '0'] = String(value).split('e');
  const shift = Number(exponent) + maxDecimals;
  const scaled = Math.round(Number(`${coefficient}e${shift}`));
  return Number(`${scaled}e-${maxDecimals}`);
};

/**
 * Format a number with up to `maxDecimals` fractional digits, never padding
 * with trailing zeros. `12` stays `12`, `12.5` stays `12.5`, `12.3456` rounds
 * to `12.346` at `maxDecimals=3`.
 */
export const formatNumberMax = (
  number: number | null,
  maxDecimals = 3,
): string | null => {
  if (number === null || isNaN(number)) return null;
  return getNumberFormatter2(0, maxDecimals).format(number);
};

export const formatCurrency = (
  number: number | null,
  fractionDigits: number,
  currency: Maybe<Currency> | undefined,
  opts?: {
    signAlways?: boolean;
    signDisplay?: Intl.NumberFormatOptions['signDisplay'];
  },
): string | null => {
  if (number === null || isNaN(number)) return null;
  const code = currency ?? Currency.EUR;

  const formatter = getCurrencyFormatter(
    code,
    fractionDigits,
    opts?.signDisplay,
  );
  const sign = opts?.signAlways && number >= 0 ? '+' : '';
  return `${sign}${applyIsoSeparators(formatter, number)}`;
};

/**
 * Format a freight rate as `{amount} {currency} {unit}` joined by NBSPs so the
 * three tokens never wrap mid-value (e.g. `105.00 USD MT`).
 */
export const formatFreightRate = (
  freightAmountInput: VoyageCharterOrderFreight['freightAmount'],
  currency: Maybe<Currency> | undefined,
  unit: string | null | undefined,
  fractionDigits = 2,
): string => {
  const freightAmount = freightAmountInput ? Number(freightAmountInput) : null;
  if (freightAmount == null || isNaN(freightAmount) || !unit) {
    return '';
  }

  return [formatNumber(freightAmount, fractionDigits), currency, unit]
    .filter(Boolean)
    .join(NBSP);
};

/** Pluralizes the last string in the template literal according to the count.
 * Examples:
 * pluralize`${count} leg` => `1 leg` or `2 legs`
 * pluralize`Optimizing ${count} leg` => `Optimizing 1 leg` or `Optimizing 2 legs` */
export const pluralize = (
  strings: TemplateStringsArray,
  countExpr: unknown,
): string => {
  const unit = strings.at(-1);
  if (!unit) {
    console.warn('pluralize: no unit');
    return `${countExpr}`;
  }
  const count = Number(countExpr);
  if (isNaN(count)) {
    console.warn(`pluralize: count ${countExpr} is not a number`);
    return `${countExpr}`;
  }
  const pluralizedUnit = count === 1 ? unit : `${unit}s`;
  return strings.length > 1
    ? `${strings[0]}${countExpr}${pluralizedUnit}`
    : `${countExpr}${pluralizedUnit}`;
};
